import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import AdaptUserService from '../../../users/services/AdaptUserService';

export const useUserVerification = () => {
  const { instance } = useMsal();
  const [userAccess, setUserAccess] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  /**
   * Verify user access against MongoDB instances
   * @param {string} email - User's email from SSO
   * @param {string} accessToken - SSO access token
   */
  const verifyUserAccess = async (email, accessToken) => {
    console.log('\n🔧 [useUserVerification] Starting user verification process...');
    console.log('📧 [useUserVerification] Email to verify:', email);
    console.log('🎟️ [useUserVerification] Token provided:', !!accessToken);
    
    setIsVerifying(true);
    setVerificationError(null);

    try {
      console.log('⏳ [useUserVerification] Calling AdaptUserService.verifyUserAccess...');
      
      const verificationResult = await AdaptUserService.verifyUserAccess(email, accessToken);
      
      console.log('📊 [useUserVerification] Verification result received:', {
        isValid: verificationResult?.isValid,
        email: verificationResult?.email,
        totalInstances: verificationResult?.totalInstances,
        instanceCount: verificationResult?.accessibleInstances?.length || 0
      });
      
      if (verificationResult?.accessibleInstances) {
        console.log('📋 [useUserVerification] Accessible instances details:', 
          verificationResult.accessibleInstances.map(inst => ({
            id: inst.id,
            name: inst.name,
            url: inst.url,
            userRole: inst.userRole
          }))
        );
      }
      
      setUserAccess(verificationResult);
      console.log('✅ [useUserVerification] User access state updated');
      
      return verificationResult;
    } catch (error) {
      console.error('❌ [useUserVerification] Error during verification:', error);
      console.log('🔍 [useUserVerification] Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      setVerificationError(error.message);
      const errorResult = {
        isValid: false,
        email: email,
        accessibleInstances: [],
        totalInstances: 0,
        error: error.message
      };
      
      setUserAccess(errorResult);
      console.log('💾 [useUserVerification] Error state saved:', errorResult);
      
      return null;
    } finally {
      setIsVerifying(false);
      console.log('🏁 [useUserVerification] Verification process completed');
    }
  };

  /**
   * Get current user from MSAL and verify their access
   */
  const verifyCurrentUser = async () => {
    const account = instance.getActiveAccount();
    if (!account) {
      setVerificationError('No active user session found');
      return null;
    }

    try {
      // Get access token
      const response = await instance.acquireTokenSilent({
        scopes: ['user.read'],
        account: account
      });

      return await verifyUserAccess(account.username, response.accessToken);
    } catch (error) {
      console.error('Failed to verify current user:', error);
      setVerificationError('Failed to verify user access');
      return null;
    }
  };

  /**
   * Clear verification state
   */
  const clearVerification = () => {
    setUserAccess(null);
    setVerificationError(null);
    setIsVerifying(false);
  };

  return {
    userAccess,
    isVerifying,
    verificationError,
    verifyUserAccess,
    verifyCurrentUser,
    clearVerification
  };
};
